'use client';

// ---------------------------------------------------------------------------
// MusicExpandedPanel — the full view
// ---------------------------------------------------------------------------
//
// Four tabs:
//
//   Library   stock tracks, plus this device's own files with their status
//   Queue     what will play, reorderable, removable
//   Playlists save the queue, play a saved one
//   Schedule  time-of-day windows
//
// Playlists and Schedule need a property in scope, so they are hidden entirely
// for a platform or chain admin rather than shown broken — those users still get
// the library and the queue.
//
// Same corner and z-index as the compact panel, taller. See MusicCompactPanel's
// header for why bottom-end and not under the trigger.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  FolderOpen,
  ListMusic,
  ListVideo,
  Minimize2,
  Music2,
  Pause,
  Play,
  Plus,
  Repeat,
  Repeat1,
  Shuffle,
  SkipBack,
  SkipForward,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { useTranslation } from '@/core/i18n';
import { useToast } from '@/core/hooks/useToast';
import { cn } from '@/core/utils/cn';

import { useMusicLibrary } from '../hooks/useMusicLibrary';
import { useMusicPlaylists } from '../hooks/useMusicPlaylists';
import { formatDuration, isLocalTrack, trackSubtitle, type Track } from '../lib/track';
import { PlaylistsTab } from './PlaylistsTab';
import { ScheduleTab } from './ScheduleTab';
import {
  selectCanGoNext,
  selectCanGoPrevious,
  selectCanPlay,
  selectCurrentTrack,
  selectQueueEntries,
  selectQueueTracks,
  type QueueEntry,
  useMusicStore,
} from '../stores/music.store';

type Tab = 'library' | 'queue' | 'playlists' | 'schedule';

const TAB_ICON: Record<Tab, typeof Music2> = {
  library: Music2,
  queue: ListMusic,
  playlists: ListVideo,
  schedule: CalendarClock,
};

export function MusicExpandedPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>('library');

  const {
    stockTracks,
    localTracks,
    isLoading,
    error,
    localFilesSupported,
    addLocalFiles,
    forgetLocalFile,
  } = useMusicLibrary();

  const current = useMusicStore(selectCurrentTrack);
  // Two views of the queue, for two different jobs:
  //   entries  index-aligned with the store, for anything addressed by index
  //   tracks   only what this device can describe, for counts and enabling
  //
  // Both selectors build a NEW array on every call, so they MUST go through
  // useShallow. zustand v5 subscribes via useSyncExternalStore, which treats a
  // fresh reference as a state change and re-renders forever — React error
  // #185. That loop crashed this panel the moment it opened, before a track
  // could be picked, which is why no stream was ever requested in production.
  const queueEntries = useMusicStore(useShallow(selectQueueEntries));
  const queueTracks = useMusicStore(useShallow(selectQueueTracks));
  const status = useMusicStore((s) => s.status);
  const positionSec = useMusicStore((s) => s.positionSec);
  const durationSec = useMusicStore((s) => s.durationSec);
  const volume = useMusicStore((s) => s.volume);
  const muted = useMusicStore((s) => s.muted);
  const shuffle = useMusicStore((s) => s.shuffle);
  const repeat = useMusicStore((s) => s.repeat);
  const currentIndex = useMusicStore((s) => s.currentIndex);
  const canNext = useMusicStore(selectCanGoNext);
  const canPrev = useMusicStore(selectCanGoPrevious);
  const canPlay = useMusicStore(selectCanPlay);

  const playNow = useMusicStore((s) => s.playNow);
  const enqueue = useMusicStore((s) => s.enqueue);
  const togglePlay = useMusicStore((s) => s.togglePlay);
  const next = useMusicStore((s) => s.next);
  const previous = useMusicStore((s) => s.previous);
  const seek = useMusicStore((s) => s.seek);
  const setVolume = useMusicStore((s) => s.setVolume);
  const toggleMute = useMusicStore((s) => s.toggleMute);
  const toggleShuffle = useMusicStore((s) => s.toggleShuffle);
  const cycleRepeat = useMusicStore((s) => s.cycleRepeat);
  const removeFromQueue = useMusicStore((s) => s.removeFromQueue);
  const clearQueue = useMusicStore((s) => s.clearQueue);
  const jumpTo = useMusicStore((s) => s.jumpTo);
  const setPanel = useMusicStore((s) => s.setPanel);

  const isPlaying = status === 'playing';

  // Playlists and schedules need a property in scope. A platform or chain admin
  // has none, so those tabs are hidden rather than shown unable to save.
  const { unavailable: propertyScopeUnavailable } = useMusicPlaylists();
  const visibleTabs: Tab[] = propertyScopeUnavailable
    ? ['library', 'queue']
    : ['library', 'queue', 'playlists', 'schedule'];

  // DERIVED, not corrected with setTab — writing state during render is the
  // cascading-render bug the lint rule exists for. If the selected tab stops
  // being visible (the property scope went away mid-session), rendering falls
  // back to Library without the stored value fighting it.
  const activeTab: Tab = visibleTabs.includes(tab) ? tab : 'library';

  /** Play this track and queue the rest of the list after it. */
  const playFrom = (list: Track[], track: Track) => {
    playNow(list.map((x) => x.id), track.id);
  };

  const handleAddFiles = async () => {
    const added = await addLocalFiles();
    // Zero means the dialog was cancelled, which needs no message.
    if (added > 0) {
      toast('success', t('music.title'), t('music.filesAdded', { count: String(added) }));
    }
  };

  return (
    <div
      id="music-expanded-panel"
      role="region"
      aria-label={t('music.title')}
      className={cn(
        'fixed bottom-4 end-4 z-[60]',
        'w-[min(26rem,calc(100vw-2rem))] max-h-[min(40rem,calc(100vh-6rem))]',
        'flex flex-col',
        'rounded-xl border border-hms-border bg-hms-card',
        'shadow-2xl shadow-black/30 backdrop-blur-xl animate-fadeIn',
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-hms-border flex-shrink-0">
        <Music2 size={14} strokeWidth={2} className="text-hms-teal flex-shrink-0" />
        <span className="text-[11px] font-semibold text-hms-textDim uppercase tracking-wider flex-1">
          {t('music.title')}
        </span>
        <button
          id="music-expanded-collapse"
          onClick={() => setPanel('compact')}
          aria-label={t('music.collapse')}
          title={t('music.collapse')}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] transition-all duration-200"
        >
          <Minimize2 size={13} strokeWidth={2} />
        </button>
        <button
          id="music-expanded-close"
          onClick={() => setPanel('closed')}
          aria-label={t('music.minimize')}
          title={t('music.minimize')}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] transition-all duration-200"
        >
          <X size={14} strokeWidth={2} />
        </button>
      </div>

      {/* Now playing + transport */}
      <div className="px-3 pt-3 pb-2 border-b border-hms-border flex-shrink-0">
        {current ? (
          <>
            <p dir="auto" className="text-[13px] font-medium text-hms-text truncate" title={current.title}>
              {current.title}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              {trackSubtitle(current) && (
                <span dir="auto" className="text-[11px] text-hms-textDim truncate">
                  {trackSubtitle(current)}
                </span>
              )}
              {isLocalTrack(current) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-hms-teal/15 text-hms-teal flex-shrink-0">
                  {t('music.deviceOnly')}
                </span>
              )}
            </div>
          </>
        ) : (
          <p className="text-[12px] text-hms-textDim">
            {canPlay ? t('music.pressPlayToStart') : t('music.nothingLoaded')}
          </p>
        )}

        <input
          id="music-expanded-seek"
          type="range"
          min={0}
          max={durationSec ?? 0}
          value={Math.min(positionSec, durationSec ?? 0)}
          step={1}
          disabled={!durationSec}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label={t('music.seek')}
          className="w-full h-1 mt-2.5 accent-hms-teal cursor-pointer disabled:cursor-default disabled:opacity-40"
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-hms-textDim tabular-nums">{formatDuration(positionSec)}</span>
          <span className="text-[10px] text-hms-textDim tabular-nums">{formatDuration(durationSec)}</span>
        </div>

        <div className="flex items-center justify-between mt-1">
          <button
            id="music-expanded-shuffle"
            onClick={toggleShuffle}
            aria-label={t('music.shuffle')}
            aria-pressed={shuffle}
            title={t('music.shuffle')}
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
              shuffle ? 'text-hms-teal bg-hms-teal/15' : 'text-hms-textSub hover:text-hms-text',
            )}
          >
            <Shuffle size={14} strokeWidth={2} />
          </button>

          <div className="flex items-center gap-1">
            <button
              id="music-expanded-prev"
              onClick={previous}
              disabled={!canPrev}
              aria-label={t('music.previous')}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] disabled:opacity-30 transition-all duration-200"
            >
              <SkipBack size={16} strokeWidth={2} />
            </button>
            <button
              id="music-expanded-play"
              onClick={togglePlay}
              disabled={!canPlay}
              aria-label={isPlaying ? t('music.pause') : t('music.play')}
              aria-busy={status === 'loading'}
              className="flex items-center justify-center w-11 h-11 rounded-full bg-hms-teal/15 text-hms-teal hover:bg-hms-teal/25 disabled:opacity-30 transition-all duration-200"
            >
              {isPlaying ? <Pause size={18} strokeWidth={2} /> : <Play size={18} strokeWidth={2} />}
            </button>
            <button
              id="music-expanded-next"
              onClick={() => next(false)}
              disabled={!canNext}
              aria-label={t('music.next')}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.05] disabled:opacity-30 transition-all duration-200"
            >
              <SkipForward size={16} strokeWidth={2} />
            </button>
          </div>

          <button
            id="music-expanded-repeat"
            onClick={cycleRepeat}
            aria-label={t('music.repeat')}
            title={t(`music.repeatMode.${repeat}`)}
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
              repeat !== 'off' ? 'text-hms-teal bg-hms-teal/15' : 'text-hms-textSub hover:text-hms-text',
            )}
          >
            {repeat === 'one' ? <Repeat1 size={14} strokeWidth={2} /> : <Repeat size={14} strokeWidth={2} />}
          </button>
        </div>

        <div className="flex items-center gap-2 mt-1.5">
          <button
            id="music-expanded-mute"
            onClick={toggleMute}
            aria-label={muted ? t('music.unmute') : t('music.mute')}
            className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text transition-all duration-200 flex-shrink-0"
          >
            {muted || volume === 0 ? <VolumeX size={14} strokeWidth={2} /> : <Volume2 size={14} strokeWidth={2} />}
          </button>
          <input
            id="music-expanded-volume"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={muted ? 0 : volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            aria-label={t('music.volume')}
            className="flex-1 h-1 accent-hms-teal cursor-pointer"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0.5 px-2 pt-2 flex-shrink-0" role="tablist">
        {visibleTabs.map((value) => {
          const Icon = TAB_ICON[value];
          return (
            <button
              key={value}
              id={`music-tab-${value}`}
              role="tab"
              aria-selected={activeTab ===value}
              onClick={() => setTab(value)}
              className={cn(
                'flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11.5px] transition-all duration-200',
                activeTab ===value
                  ? 'bg-white/[0.06] text-hms-text font-medium'
                  : 'text-hms-textSub hover:text-hms-text',
              )}
            >
              <Icon size={12} strokeWidth={2} />
              {value === 'queue' ? `${t('music.queue')} (${queueTracks.length})` : t(`music.${value}`)}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-y-auto px-2 py-2 min-h-[8rem]">
        {activeTab ==='library' && (
          <LibraryTab
            stockTracks={stockTracks}
            localTracks={localTracks}
            isLoading={isLoading}
            error={error}
            localFilesSupported={localFilesSupported}
            onAddFiles={handleAddFiles}
            onForget={forgetLocalFile}
            onPlay={playFrom}
            onEnqueue={(track) => enqueue([track.id])}
          />
        )}
        {activeTab ==='queue' && (
          <QueueTab
            entries={queueEntries}
            currentIndex={currentIndex}
            onJump={jumpTo}
            onRemove={removeFromQueue}
            onClear={clearQueue}
          />
        )}
        {activeTab ==='playlists' && <PlaylistsTab />}
        {activeTab ==='schedule' && <ScheduleTab />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function LibraryTab({
  stockTracks,
  localTracks,
  isLoading,
  error,
  localFilesSupported,
  onAddFiles,
  onForget,
  onPlay,
  onEnqueue,
}: {
  stockTracks: Track[];
  localTracks: Track[];
  isLoading: boolean;
  error: string | null;
  localFilesSupported: boolean;
  onAddFiles: () => void;
  onForget: (id: string) => void;
  onPlay: (list: Track[], track: Track) => void;
  onEnqueue: (track: Track) => void;
}) {
  const { t } = useTranslation();
  const all = [...stockTracks, ...localTracks];

  return (
    <div className="space-y-3">
      {error && (
        <p className="flex items-start gap-1.5 text-[11px] text-hms-gold px-1">
          <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
          {error}
        </p>
      )}

      <section>
        <p className="px-1 pb-1 text-[10px] font-semibold text-hms-textDim uppercase tracking-wider">
          {t('music.stockLibrary')}
        </p>
        {isLoading ? (
          <p className="px-1 text-[12px] text-hms-textDim">{t('music.loading')}</p>
        ) : stockTracks.length === 0 ? (
          <p className="px-1 text-[12px] text-hms-textDim">{t('music.noStockTracks')}</p>
        ) : (
          <ul id="music-stock-list">
            {stockTracks.map((track) => (
              <TrackRow
                key={track.id}
                track={track}
                onPlay={() => onPlay(all, track)}
                onEnqueue={() => onEnqueue(track)}
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between px-1 pb-1">
          <p className="text-[10px] font-semibold text-hms-textDim uppercase tracking-wider">
            {t('music.myDevice')}
          </p>
          {localFilesSupported && (
            <button
              id="music-add-local"
              onClick={onAddFiles}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-hms-teal hover:bg-hms-teal/10 transition-all duration-200"
            >
              <FolderOpen size={12} strokeWidth={2} />
              {t('music.addFiles')}
            </button>
          )}
        </div>

        {!localFilesSupported ? (
          // Said plainly rather than hiding the feature: on Firefox and Safari
          // there is no way to remember a file, and appearing to save one that
          // silently disappears is worse than saying so.
          <p className="px-1 text-[11px] text-hms-textDim">{t('music.localUnsupported')}</p>
        ) : localTracks.length === 0 ? (
          <p className="px-1 text-[12px] text-hms-textDim">{t('music.noLocalFiles')}</p>
        ) : (
          <ul id="music-local-list">
            {localTracks.map((track) => (
              <TrackRow
                key={track.id}
                track={track}
                onPlay={() => onPlay(all, track)}
                onEnqueue={() => onEnqueue(track)}
                onForget={() => onForget(track.id)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function TrackRow({
  track,
  onPlay,
  onEnqueue,
  onForget,
}: {
  track: Track;
  onPlay: () => void;
  onEnqueue: () => void;
  onForget?: () => void;
}) {
  const { t } = useTranslation();
  const local = isLocalTrack(track);
  const missing = local && track.status === 'missing';
  const needsPermission = local && track.status === 'needs-permission';
  // Local ids are `local:<uuid>`; a colon is not valid in a CSS id selector.
  const rowId = `music-track-${track.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  return (
    <li id={rowId} className="group flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-white/[0.04] transition-all duration-200">
      <button
        id={`${rowId}-play`}
        onClick={onPlay}
        disabled={missing}
        aria-label={t('music.playTrack', { title: track.title })}
        className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-teal disabled:opacity-30 flex-shrink-0 transition-all duration-200"
      >
        <Play size={13} strokeWidth={2} />
      </button>

      <div className="flex-1 min-w-0">
        <p dir="auto" className="text-[12.5px] text-hms-text truncate" title={track.title}>
          {track.title}
        </p>
        <div className="flex items-center gap-1.5">
          {trackSubtitle(track) && (
            <span dir="auto" className="text-[10.5px] text-hms-textDim truncate">
              {trackSubtitle(track)}
            </span>
          )}
          {missing && (
            // Requirement 6, surfaced: the file was moved or deleted, and this is
            // where the user finds out — not as a silent failure at play time.
            <span className="text-[10px] text-hms-red flex-shrink-0">{t('music.fileNotFound')}</span>
          )}
          {needsPermission && (
            <span className="text-[10px] text-hms-gold flex-shrink-0">{t('music.needsAccess')}</span>
          )}
        </div>
      </div>

      <span className="text-[10px] text-hms-textDim tabular-nums flex-shrink-0">
        {formatDuration(track.durationSec)}
      </span>

      <button
        id={`${rowId}-enqueue`}
        onClick={onEnqueue}
        disabled={missing}
        aria-label={t('music.addToQueue')}
        title={t('music.addToQueue')}
        className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-text disabled:opacity-30 opacity-0 group-hover:opacity-100 focus:opacity-100 flex-shrink-0 transition-all duration-200"
      >
        <Plus size={13} strokeWidth={2} />
      </button>

      {onForget && (
        <button
          id={`${rowId}-forget`}
          onClick={onForget}
          aria-label={t('music.forgetFile')}
          title={t('music.forgetFile')}
          className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-red opacity-0 group-hover:opacity-100 focus:opacity-100 flex-shrink-0 transition-all duration-200"
        >
          <Trash2 size={13} strokeWidth={2} />
        </button>
      )}
    </li>
  );
}

function QueueTab({
  entries,
  currentIndex,
  onJump,
  onRemove,
  onClear,
}: {
  /**
   * One per queue position, index-aligned with the store's `queue`.
   *
   * NOT a filtered list of known tracks: `onRemove`/`onJump` take an index into
   * `queue`, so dropping entries here would make every row below a gap act on
   * the wrong track.
   */
  entries: QueueEntry[];
  currentIndex: number;
  onJump: (index: number) => void;
  onRemove: (index: number) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return <p className="px-1 text-[12px] text-hms-textDim">{t('music.emptyQueue')}</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-end px-1 pb-1">
        <button
          id="music-queue-clear"
          onClick={onClear}
          className="text-[11px] text-hms-textSub hover:text-hms-red transition-all duration-200"
        >
          {t('music.clearQueue')}
        </button>
      </div>
      <ul id="music-queue-list">
        {entries.map(({ id, track }, index) => (
          <li
            key={`${id}-${index}`}
            className={cn(
              'group flex items-center gap-2 px-1.5 py-1.5 rounded-lg transition-all duration-200',
              index === currentIndex ? 'bg-hms-teal/10' : 'hover:bg-white/[0.04]',
              !track && 'opacity-60',
            )}
          >
            <button
              onClick={() => onJump(index)}
              // An unknown entry cannot be played here, but it keeps its row and
              // its index so everything below it still removes the right track.
              disabled={!track}
              aria-label={
                track
                  ? t('music.playTrack', { title: track.title })
                  : t('music.notOnThisDevice')
              }
              className="flex-1 min-w-0 text-start disabled:cursor-default"
            >
              <p
                dir="auto"
                className={cn(
                  'text-[12.5px] truncate',
                  index === currentIndex ? 'text-hms-teal font-medium' : 'text-hms-text',
                )}
                title={track ? track.title : t('music.notOnThisDevice')}
              >
                {track ? track.title : t('music.notOnThisDevice')}
              </p>
              {track && trackSubtitle(track) && (
                <span dir="auto" className="text-[10.5px] text-hms-textDim truncate">
                  {trackSubtitle(track)}
                </span>
              )}
            </button>

            <span className="text-[10px] text-hms-textDim tabular-nums flex-shrink-0">
              {formatDuration(track ? track.durationSec : null)}
            </span>

            <button
              onClick={() => onRemove(index)}
              aria-label={t('music.removeFromQueue')}
              title={t('music.removeFromQueue')}
              className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-red opacity-0 group-hover:opacity-100 focus:opacity-100 flex-shrink-0 transition-all duration-200"
            >
              <X size={13} strokeWidth={2} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
